import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useStoreState, useStoreActions } from 'easy-peasy';
import {
  Box,
  Grid,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
} from '@material-ui/core';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import PublishIcon from '@material-ui/icons/Publish';
import DisplayBreadcrumbs from '../../components/displayBreadcrumbs/displayBreadcrumbs';
import { useParams } from 'react-router-dom';
import { Script, ScriptTestItem } from './types';
import ScriptTestDetailModal from './components/ScriptTestDetailModal';
import styles from './ScriptTesting.module.scss';

const parseTestItem = (item: any, idx: number): ScriptTestItem => ({
  id: item.id || `test-${idx}`,
  name: item.name || `Test ${idx + 1}`,
  scriptId: item.scriptId || '',
  scriptName: item.scriptName || '',
  sample:
    typeof item.sample === 'string'
      ? item.sample
      : JSON.stringify(item.sample || {}),
  target:
    typeof item.target === 'string'
      ? item.target
      : JSON.stringify(item.target || {}),
  status: 'pending' as const,
});

const compareResults = (output: string, target: string): 'pass' | 'fail' => {
  try {
    const outputObj = JSON.parse(output);
    const targetObj = JSON.parse(target);
    return JSON.stringify(outputObj) === JSON.stringify(targetObj) ? 'pass' : 'fail';
  } catch {
    return output === target ? 'pass' : 'fail';
  }
};

const ScriptTesting: React.FC = () => {
  const { orgId: orgIdParam } = useParams<{ orgId?: string }>();
  const isOrgContext = !!orgIdParam;

  const inputFile = useRef<HTMLInputElement>(null);
  const [testItems, setTestItems] = useState<ScriptTestItem[]>([]);
  const [isRunningAll, setIsRunningAll] = useState(false);
  const [detailModal, setDetailModal] = useState<{
    open: boolean;
    title: string;
    content: string;
  }>({
    open: false,
    title: '',
    content: '',
  });

  const storeOrgId = useStoreState(
    (state: any) => state.organization.organizationId,
  );
  const orgId = orgIdParam ? decodeURIComponent(orgIdParam) : storeOrgId;
  const scriptList: Script[] = useStoreState(
    (state: any) => state.script.scriptList,
  );
  const scriptAction = useStoreActions((actions: any) => actions.script);
  const commonAction = useStoreActions((actions: any) => actions.common);

  // Load scripts on mount so we can look up scriptText by scriptId
  useEffect(() => {
    scriptAction.getScriptList({ orgId });
  }, [orgId, scriptAction]);

  const handleLoadFile = () => {
    inputFile.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      if (!e.target?.result) return;
      try {
        const data = JSON.parse(e.target.result as string);
        if (Array.isArray(data)) {
          setTestItems(data.map(parseTestItem));
        }
      } catch {
        commonAction.setSubmitSnackbar({
          show: true,
          msg: 'Failed to parse test file. Ensure it is valid JSON.',
          isError: true,
        });
      }
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  const runSingleTest = useCallback(
    async (item: ScriptTestItem, index: number) => {
      setTestItems((prev) =>
        prev.map((t, i) =>
          i === index ? { ...t, status: 'running' as const } : t,
        ),
      );

      try {
        // Look up the actual javascript code from the loaded script list
        const script = scriptList.find((s) => s.id === item.scriptId);
        const javascriptCode = script?.javascriptCode || '';

        const result = await scriptAction.testScript({
          orgId,
          id: item.scriptId,
          javascriptCode,
          data: item.sample,
          commonAction,
        });

        const output = result?.data || '';
        const status = result?.ok ? compareResults(output, item.target) : 'fail';

        setTestItems((prev) =>
          prev.map((t, i) =>
            i === index
              ? {
                  ...t,
                  status,
                  output,
                  validationMessage: result?.validationMessage || '',
                }
              : t,
          ),
        );
      } catch {
        setTestItems((prev) =>
          prev.map((t, i) =>
            i === index ? { ...t, status: 'fail' as const } : t,
          ),
        );
      }
    },
    [orgId, scriptList, scriptAction, commonAction],
  );

  const handleRunAll = useCallback(async () => {
    setIsRunningAll(true);
    for (let i = 0; i < testItems.length; i++) {
      await runSingleTest(testItems[i], i);
    }
    setIsRunningAll(false);
  }, [testItems, runSingleTest]);

  const handleCellClick = (title: string, content: string) => {
    setDetailModal({ open: true, title, content });
  };

  const getStatusChip = (status?: string) => {
    switch (status) {
      case 'pass':
        return <Chip label="Pass" size="small" className={styles.statusPass} />;
      case 'fail':
        return <Chip label="Fail" size="small" className={styles.statusFail} />;
      case 'running':
        return <CircularProgress size={16} />;
      default:
        return <Chip label="Pending" size="small" variant="outlined" />;
    }
  };

  const formatPreview = (jsonStr: string) => {
    try {
      const parsed = JSON.parse(jsonStr);
      const str = JSON.stringify(parsed);
      return str.length > 50 ? str.substring(0, 50) + '...' : str;
    } catch {
      return jsonStr.length > 50 ? jsonStr.substring(0, 50) + '...' : jsonStr;
    }
  };

  return (
    <Grid container direction="row">
      <Grid item md={12} xs={12}>
        <DisplayBreadcrumbs
          items={
            isOrgContext
              ? [
                  { title: 'Tenants', link: '/organization' },
                  {
                    title: 'Tenants Details',
                    link: `/organization/${encodeURIComponent(orgId)}`,
                  },
                  {
                    title: 'Transformations',
                    link: `/organization/${encodeURIComponent(orgId)}`,
                  },
                  { title: 'Testing', link: '' },
                ]
              : [
                  { title: 'Scripts', link: '/scripts' },
                  { title: 'Testing', link: '' },
                ]
          }
        />
      </Grid>

      <Grid item md={12} style={{ padding: '30px 20px 10px 20px' }}>
        <Box className={styles.pageWrapper}>
          <Grid
            container
            direction="row"
            alignItems="center"
            style={{ marginBottom: 16 }}
          >
            <Grid item xs={8}>
              <Typography variant="h6">Script Testing</Typography>
            </Grid>
            <Grid item xs={4} style={{ textAlign: 'right' }}>
              <input
                type="file"
                accept=".json"
                ref={inputFile}
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
              <Button
                variant="outlined"
                startIcon={<PublishIcon />}
                onClick={handleLoadFile}
                size="small"
                style={{ marginRight: 8 }}
              >
                Load from file
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<PlayArrowIcon />}
                onClick={handleRunAll}
                disabled={isRunningAll || testItems.length === 0}
                size="small"
              >
                {isRunningAll ? 'Running...' : 'Run All'}
              </Button>
            </Grid>
          </Grid>

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Script</TableCell>
                  <TableCell>Sample</TableCell>
                  <TableCell>Target</TableCell>
                  <TableCell>Output</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {testItems.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="body2" color="textSecondary">
                        Load a test file (JSON array) to begin testing
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {testItems.map((item, idx) => (
                  <TableRow key={item.id}>
                    <TableCell>{item.name}</TableCell>
                    <TableCell>{item.scriptName}</TableCell>
                    <TableCell
                      className={styles.clickableCell}
                      onClick={() => handleCellClick('Sample', item.sample)}
                    >
                      {formatPreview(item.sample)}
                    </TableCell>
                    <TableCell
                      className={styles.clickableCell}
                      onClick={() => handleCellClick('Target', item.target)}
                    >
                      {formatPreview(item.target)}
                    </TableCell>
                    <TableCell
                      className={styles.clickableCell}
                      onClick={() =>
                        item.output && handleCellClick('Output', item.output)
                      }
                    >
                      {item.output ? formatPreview(item.output) : '-'}
                    </TableCell>
                    <TableCell>{getStatusChip(item.status)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Grid>

      <ScriptTestDetailModal
        open={detailModal.open}
        title={detailModal.title}
        content={detailModal.content}
        onClose={() => setDetailModal({ open: false, title: '', content: '' })}
      />
    </Grid>
  );
};

export default ScriptTesting;
